import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useBranding } from './BrandingContext';

// =============================================================================
// TYPES
// =============================================================================

export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const STORAGE_KEY = 'theme';
const DEFAULT_THEME: Theme = 'dark';

// =============================================================================
// CONTEXT
// =============================================================================

const ThemeContext = createContext<ThemeContextType | null>(null);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { defaultTheme: orgDefaultTheme, isLoading: brandingLoading } = useBranding();

  // Initialize from localStorage for flash-free page load. If localStorage is
  // empty, fall back to DEFAULT_THEME for the first render, then switch to the
  // org default (if any) once branding loads. User preference wins over both
  // later.
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return DEFAULT_THEME;
  });

  // Apply org default when: branding has loaded, visitor hasn't explicitly
  // picked a theme (no localStorage entry), and no logged-in user preference.
  useEffect(() => {
    if (brandingLoading) return;
    if (!orgDefaultTheme) return;
    if (user?.preferences?.theme) return; // user override handled below
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return; // visitor already chose
    setThemeState(orgDefaultTheme);
  }, [orgDefaultTheme, brandingLoading, user?.preferences?.theme]);

  // Sync with user preferences once loaded
  useEffect(() => {
    if (user?.preferences?.theme) {
      const userTheme = user.preferences.theme as string;
      if (userTheme === 'light' || userTheme === 'dark') {
        setThemeState(userTheme);
        localStorage.setItem(STORAGE_KEY, userTheme);
      }
    }
  }, [user?.preferences?.theme]);

  // Apply data-theme attribute to body whenever theme changes
  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
    document.body.setAttribute('data-theme', newTheme);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
